import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Shell from "@/components/app/Shell";
import api from "@/lib/api";
import { TRIBES } from "@/lib/tribes";
import { useAuth } from "@/context/AuthContext";
import { useOverride } from "@/context/ContentContext";
import { Heart, PlusCircle, ArrowRight, Lock, Sparkles, Flame, Trophy, Columns2 } from "lucide-react";
import TribeEmblem from "@/components/app/TribeEmblem";

// Fallback when the admin hasn't set the real Kajabi add-on Offer URL yet.
// Admin can override this at /admin/content/bulk-videos → Support links → "Support — Add-on Family Slot Checkout URL".
const ADDON_CHECKOUT_FALLBACK = "https://YOUR-KAJABI-ADDON-CHECKOUT-URL";

export default function Dashboard() {
  const { user } = useAuth();
  const [children, setChildren] = useState([]);
  const [streaks, setStreaks] = useState({}); // childId → {current, longest, total}
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();
  const addonCheckoutUrl = useOverride("support", "addon_checkout", "link_url", ADDON_CHECKOUT_FALLBACK);

  useEffect(() => {
    api.get("/children").then(async ({ data }) => {
      setChildren(data);
      // Pull streaks in parallel — sibling leaderboard renders when we have 2+ kids.
      if (data.length >= 2) {
        try {
          const results = await Promise.all(data.map((c) => api.get(`/streak/${c.id}`).then((r) => [c.id, r.data]).catch(() => [c.id, null])));
          setStreaks(Object.fromEntries(results.filter(([, v]) => v)));
        } catch (_) { /* ignore, leaderboard just won't render */ }
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) return <Shell><div className="text-ink-mid">Loading…</div></Shell>;

  const paidSlots = Number.isFinite(user?.paid_slots) ? user.paid_slots : 0;
  // Every family gets 1 free child slot implicitly; paid_slots is add-on siblings.
  const allowed = 1 + paidSlots;
  const hasFreeSlot = children.length < allowed;

  if (children.length === 0) {
    return (
      <Shell>
        <div className="max-w-2xl mx-auto text-center py-16">
          <h1 className="font-display text-3xl font-bold text-navy">Welcome, {user?.name?.split(" ")[0] || "friend"}.</h1>
          <p className="text-ink-mid mt-3">Let's enroll your child to begin the journey.</p>
          <Link to="/enroll" data-testid="dashboard-enroll-cta" className="mt-6 inline-flex items-center gap-2 bg-gold text-ink-dark px-6 py-3 rounded-full font-semibold hover:bg-gold-light">
            <PlusCircle size={16} /> Enroll a child
          </Link>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="mb-8 flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-mauve font-semibold">Family</div>
          <h1 className="font-display text-3xl font-bold text-navy mt-1">Welcome back, {user?.name}</h1>
          {paidSlots > 0 && (
            <div className="text-xs text-ink-mid mt-2" data-testid="dashboard-slots-summary">
              <span className="font-semibold text-navy">{children.length}</span> enrolled · <span className="font-semibold text-navy">{allowed}</span> total {allowed === 1 ? "spot" : "spots"} available
            </div>
          )}
        </div>
        {children.length >= 2 && (
          <Link
            to="/family/compare"
            data-testid="dashboard-compare-btn"
            className="inline-flex items-center gap-2 bg-navy text-warm-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-navy-deep"
          >
            <Columns2 size={14} /> Compare side by side
          </Link>
        )}
      </div>
      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 grid gap-4">
          {children.map((c) => {
            const tribe = TRIBES[c.tribe] || TRIBES.neutral;
            const progress = ((c.current_session - 1) / 12) * 100;
            return (
              <Link key={c.id} to={`/child/${c.id}`} data-testid={`child-card-${c.id}`} className="block bg-warm-white border border-edge rounded-3xl p-6 md:p-8 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(44,45,91,0.08)] transition-all">
                <div className="flex items-center justify-between">
                  <div className="flex items-start gap-3">
                    <TribeEmblem tribeKey={c.tribe} child={c} size={44} testid={`child-card-emblem-${c.id}`} />
                    <div>
                      {(c.is_self || c.tribe !== "neutral") && (
                        <div className="text-xs uppercase tracking-widest font-bold" style={{ color: tribe.subtle }}>{c.is_self ? "Self-enrolled" : tribe.name}</div>
                      )}
                      <div className="font-display text-2xl font-bold text-navy mt-1">{c.name}</div>
                      <div className="text-sm text-ink-mid mt-1">{c.age ? `Age ${c.age} · ` : ""}Session {c.current_session} of 12</div>
                    </div>
                  </div>
                  <ArrowRight className="text-gold" />
                </div>
                <div className="mt-6 h-2 rounded-full bg-cream-dark overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${progress}%`, background: tribe.accent }} />
                </div>
              </Link>
            );
          })}
          {hasFreeSlot ? (
            <Link to="/enroll" data-testid="add-child-btn" className="block bg-cream border-2 border-dashed border-edge rounded-3xl p-6 text-center hover:border-gold transition-all">
              <div className="inline-flex items-center gap-2 text-navy font-semibold"><PlusCircle size={16} /> Enroll another family member</div>
              <div className="text-xs text-ink-soft mt-1">You have a paid slot ready — siblings can each have their own tribe and journey</div>
            </Link>
          ) : (
            <div data-testid="add-child-locked-card" className="block bg-mauve-pale border-2 border-dashed border-mauve rounded-3xl p-6">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-2xl bg-mauve text-warm-white flex items-center justify-center flex-shrink-0">
                  <Lock size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-navy text-lg">You've used all your enrolled spots</div>
                  <p className="text-sm text-ink-mid mt-1 leading-relaxed">Their own tribe. Their own progress. The proven 12-session program, personalized just for them.</p>
                  <a
                    href={addonCheckoutUrl}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="add-child-checkout-cta"
                    className="mt-4 inline-flex items-center gap-2 bg-gold text-ink-dark px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-gold-light"
                  >
                    <Sparkles size={14} /> Add another spot
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="bg-warm-white border border-edge rounded-3xl p-6 space-y-6">
          {children.length >= 2 && Object.keys(streaks).length >= 2 && (() => {
            const rows = children
              .map((c) => ({ c, s: streaks[c.id] || { current: 0, longest: 0, total: 0 } }))
              .sort((a, b) => (b.s.current - a.s.current) || (b.s.longest - a.s.longest));
            const topCurrent = rows[0]?.s.current || 0;
            const anyone = rows.some((r) => r.s.current > 0);
            const tiedCount = rows.filter((r) => r.s.current === topCurrent).length;
            const tiedAtTop = anyone && tiedCount > 1;
            return (
              <div data-testid="family-streak-leaderboard">
                <div className="flex items-center gap-2">
                  <Trophy className="text-mauve" size={18} />
                  <div className="text-xs uppercase tracking-widest text-ink-soft font-semibold">Family streaks</div>
                </div>
                <div className="text-xs text-ink-mid mt-2 italic" data-testid="family-streak-byline">
                  {!anyone
                    ? "Whoever practices first today lights the leaderboard."
                    : tiedAtTop
                    ? "Tied — cheer each other on."
                    : "One practice today keeps a streak alive."}
                </div>
                <div className="mt-3 space-y-2">
                  {rows.map(({ c, s }) => {
                    const tribe = TRIBES[c.tribe] || TRIBES.neutral;
                    const isTop = s.current === topCurrent && s.current > 0;
                    const isSoloLeader = isTop && !tiedAtTop;
                    const isTied = isTop && tiedAtTop;
                    return (
                      <Link
                        key={c.id}
                        to={`/child/${c.id}`}
                        data-testid={`streak-row-${c.id}`}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all ${isSoloLeader ? "bg-gold/10 border-gold" : isTied ? "bg-mauve-pale border-mauve" : "bg-cream border-edge hover:border-mauve"}`}
                      >
                        <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ background: tribe.accent }}>
                          <Flame size={14} className="text-warm-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-navy truncate">{c.name}</div>
                          {(c.is_self || c.tribe !== "neutral") && (
                            <div className="text-[10px] uppercase tracking-widest text-ink-soft font-semibold truncate">
                              {c.is_self ? "Self-enrolled" : tribe.name}
                            </div>
                          )}
                        </div>
                        <div className="text-right shrink-0">
                          <div className="font-display text-xl font-bold text-navy leading-none" data-testid={`streak-row-${c.id}-current`}>{s.current}</div>
                          <div className="text-[10px] uppercase tracking-widest text-ink-soft font-semibold mt-0.5">
                            {s.current === 1 ? "day" : "days"}
                          </div>
                        </div>
                        {isSoloLeader && (
                          <div className="ml-1 shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-gold text-ink-dark" data-testid={`streak-row-${c.id}-leader`}>
                            <Trophy size={10} /> Lead
                          </div>
                        )}
                        {isTied && (
                          <div className="ml-1 shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-mauve text-warm-white" data-testid={`streak-row-${c.id}-tied`}>
                            <Heart size={10} /> Tied
                          </div>
                        )}
                      </Link>
                    );
                  })}
                </div>
                <div className="mt-4 h-px bg-edge" />
              </div>
            );
          })()}
          <div>
            <Heart className="text-mauve" />
            <div className="mt-3 text-xs uppercase tracking-widest text-ink-soft font-semibold">Donations given</div>
            <div className="font-display text-4xl font-bold text-navy mt-1">${user?.donations_given || 0}</div>
            <p className="text-sm text-ink-mid mt-2">Each proof photo you submit with a certificate adds $1 to a shared fund.</p>
          </div>
        </div>
      </div>
    </Shell>
  );
}
